import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserStore } from '../../stores';
// Imported from its own module, not the barrel: test files that mock the
// barrel with only useUserStore would otherwise lose the header entirely.
import { useSettingsStore } from '../../stores/useSettingsStore';
import { Settings as GearIcon } from 'lucide-react';
import SettingsSheet from './SettingsSheet';
// After SettingsSheet, which already loads this barrel: the import adds no CSS
// and cannot move the cascade.
import { countCalendarsNeedingAttention } from '../settings';
import ProfilePanel from './ProfilePanel';
import AuthGateModal from '../chat/AuthGateModal';
import './GlobalHeader.css';
// The gear mark's CSS (.gear-need, .gear-need-mark) lives in pages/Settings.css, loaded via the eager Settings.tsx import in App.tsx.

// The gear's brick "!" (Almanac D14) reads calendar health through the one
// shared definition, `countCalendarsNeedingAttention` in components/settings:
// a paused or errored calendar, and nothing else — never Plus.

const GlobalHeader: React.FC = () => {
  const user = useUserStore((s) => s.user);
  const location = useLocation();
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showAuthGate, setShowAuthGate] = useState(false);
  const googleCalendars = useSettingsStore((s) => s.googleCalendars);
  const subscribedCalendars = useSettingsStore((s) => s.subscribedCalendars);
  const fetchSettings = useSettingsStore((s) => s.fetchAll);
  const needsAttention = !!user
    && countCalendarsNeedingAttention(googleCalendars, subscribedCalendars) > 0;

  // The settings store is the one place calendar health lives; its own
  // freshness window keeps this from refetching on every mount.
  const userId = user?.id;
  useEffect(() => {
    if (userId) fetchSettings();
  }, [userId, fetchSettings]);

  // Determine tab title from current route
  const getTitle = () => {
    const path = location.pathname;
    if (path === '/' || path === '') return 'Now';
    if (path.startsWith('/explore')) return 'Explore';
    if (path === '/plan') return 'My Plans';
    if (path.startsWith('/itinerary')) return 'Plan';
    if (path.startsWith('/plans/p/')) return 'Plan';
    if (path.startsWith('/settings')) return 'Settings';
    return 'VizDay';
  };

  // Don't show on certain pages
  const hiddenPaths = ['/login', '/signup', '/reset-password', '/verify-email'];
  if (hiddenPaths.some(p => location.pathname.startsWith(p))) return null;

  const initials = user
    ? `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() || '?'
    : '';

  return (
    <>
      <header className="global-header">
        <h1 className="global-header-title">{getTitle()}</h1>
        <div className="global-header-actions">
          <button
            className={`global-header-btn${needsAttention ? ' gear-need' : ''}`}
            onClick={() => setShowSettings(true)}
            aria-label={needsAttention ? 'Settings, a calendar needs you' : 'Settings'}
          >
            <GearIcon size={20} />
            {needsAttention && (
              <span className="gear-need-mark" data-testid="gear-need-mark" aria-hidden="true">!</span>
            )}
          </button>
          {user ? (
            <button className="global-header-avatar" onClick={() => setShowProfile(true)} aria-label="Profile">
              {initials}
            </button>
          ) : (
            <button className="global-header-signup" onClick={() => setShowAuthGate(true)}>
              Sign up
            </button>
          )}
        </div>
      </header>

      {showSettings && <SettingsSheet onClose={() => setShowSettings(false)} />}
      {showProfile && <ProfilePanel onClose={() => setShowProfile(false)} />}
      {showAuthGate && (
        <AuthGateModal
          msgCount={0}
          onAuth={() => { setShowAuthGate(false); navigate('/login'); }}
          onDismiss={() => setShowAuthGate(false)}
        />
      )}
    </>
  );
};

export default GlobalHeader;
